import React, { forwardRef, useState } from 'react';

export type ListActionKind = 'edit' | 'delete' | 'link';

export interface ListActionButtonProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'children'
> {
  kind: ListActionKind;
  /** Accessible name — required so actions are not icon-only for assistive tech. */
  'aria-label': string;
}

const ICONS: Record<ListActionKind, React.ReactNode> = {
  edit: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
    </svg>
  ),
  delete: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  ),
  link: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  ),
};

/**
 * Inline list edit/delete/link control with a solid hit target and
 * stroke icons that stay readable on both light and dark surfaces.
 */
export const ListActionButton = forwardRef<HTMLButtonElement, ListActionButtonProps>(
  ({ kind, style, onMouseEnter, onMouseLeave, type = 'button', title, ...props }, ref) => {
    const [hovered, setHovered] = useState(false);

    const tone =
      kind === 'delete'
        ? {
            color: hovered ? 'var(--z-bad-ink)' : 'var(--z-muted-2)',
            background: hovered ? 'var(--z-bad-bg)' : 'var(--z-fill-2)',
          }
        : {
            color: hovered ? 'var(--z-text-2)' : 'var(--z-muted-2)',
            background: hovered ? 'var(--z-fill)' : 'var(--z-fill-2)',
          };

    return (
      <button
        ref={ref}
        type={type}
        {...props}
        title={title ?? props['aria-label']}
        onMouseEnter={(e) => {
          setHovered(true);
          onMouseEnter?.(e);
        }}
        onMouseLeave={(e) => {
          setHovered(false);
          onMouseLeave?.(e);
        }}
        style={{
          flex: '0 0 auto',
          width: 28,
          height: 28,
          minWidth: 28,
          minHeight: 28,
          padding: 0,
          borderRadius: 8,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: props.disabled ? 'not-allowed' : 'pointer',
          color: tone.color,
          background: tone.background,
          border: 'none',
          boxSizing: 'border-box',
          transition: 'background 120ms ease, color 120ms ease',
          ...style,
        }}
      >
        {ICONS[kind]}
      </button>
    );
  },
);

ListActionButton.displayName = 'ListActionButton';
